import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Info } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingPage() {
  const [pricing, setPricing] = useState([]);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pricingRes, propRes] = await Promise.all([
          axios.get(`${API}/pricing`),
          axios.get(`${API}/property`),
        ]);
        setPricing(pricingRes.data);
        setProperty(propRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 container-padding">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-caption text-[#D4AF37] mb-4">Pricing</p>
          <h1 className="heading-display text-[#1C1C1C] mb-6">
            Seasonal Rates
          </h1>
          <p className="text-body-lg text-[#66605B]">
            Big Bear Estate offers weekly rentals from May through November. 
            Rates vary by season to reflect peak demand periods.
          </p>
        </motion.div>
      </section>

      {/* Pricing Grid */}
      <section className="container-padding pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pricing.map((season, idx) => {
            const isPeak = season.season_name.toLowerCase().includes('peak');
            return (
              <motion.div
                key={season.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`relative p-8 rounded-sm ${
                  isPeak 
                    ? 'bg-[#1B4D3E] text-white' 
                    : 'bg-white shadow-sm'
                }`}
              >
                {isPeak && (
                  <div className="absolute top-4 right-4 bg-[#D4AF37] text-[#1C1C1C] px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-sm">
                    Peak Season
                  </div>
                )}
                <h3 className={`font-serif text-xl mb-2 ${isPeak ? 'text-white' : 'text-[#1C1C1C]'}`}>
                  {season.season_name}
                </h3>
                <p className={`text-sm mb-6 ${isPeak ? 'text-white/70' : 'text-[#66605B]'}`}>
                  {season.months}
                </p>
                <div className="mb-6">
                  <span className={`font-serif text-4xl ${isPeak ? 'text-[#D4AF37]' : 'text-[#1B4D3E]'}`}>
                    {formatPrice(season.price)}
                  </span>
                  <span className={`text-sm ${isPeak ? 'text-white/70' : 'text-[#66605B]'}`}>
                    {' '}/ week
                  </span>
                </div>
                <Link
                  to="/booking"
                  className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-sm text-sm font-bold uppercase tracking-wider transition-colors ${
                    isPeak
                      ? 'bg-[#D4AF37] text-[#1C1C1C] hover:bg-[#c9a432]'
                      : 'bg-[#1B4D3E] text-white hover:bg-[#153E32]'
                  }`}
                >
                  Check Availability
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* What's Included */}
      <section className="bg-[#F0EBE5] section-padding">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-caption text-[#D4AF37] mb-4">What's Included</p>
              <h2 className="heading-section text-[#1C1C1C]">
                Everything You Need
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-6">
              {[
                '7 Bedrooms, sleeps 15',
                '5 Bathrooms (3 ensuite)',
                'In-ground heated pool',
                'Hot tub',
                'HD projection home theatre',
                'Fitness gym',
                'High-speed WiFi',
                'Air conditioning',
                'Gourmet kitchen',
                'Canoe, kayak & paddle boat',
                'Large dock with swim ladders',
                'Water trampoline',
                'Weekly cleaning service',
                'Linens & towels provided',
                'Emergency power generator',
                'Property manager on call',
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.03 }}
                  className="flex items-center gap-3"
                >
                  <Check className="w-5 h-5 text-[#1B4D3E] flex-shrink-0" />
                  <span className="text-[#1C1C1C]">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Deposit & Terms */}
      <section className="section-padding">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-caption text-[#D4AF37] mb-4">Terms</p>
              <h2 className="heading-section text-[#1C1C1C]">
                Booking Information
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-sm shadow-sm">
                <h3 className="font-serif text-xl text-[#1C1C1C] mb-4">Check-in / Check-out</h3>
                <div className="space-y-4 text-[#66605B]">
                  <p>
                    <strong className="text-[#1C1C1C]">Check-in:</strong>{' '}
                    {property?.check_in || 'Friday 4:30 pm'}
                  </p>
                  <p>
                    <strong className="text-[#1C1C1C]">Check-out:</strong>{' '}
                    {property?.check_out || 'Friday 11:00 am'}
                  </p>
                  <p className="text-sm">
                    A pre-arranged time will be set to meet you at the premises, 
                    show you around and give you the keys.
                  </p>
                </div>
              </div>

              <div className="bg-white p-8 rounded-sm shadow-sm">
                <h3 className="font-serif text-xl text-[#1C1C1C] mb-4">Deposits</h3>
                <div className="space-y-4 text-[#66605B]">
                  <p>
                    <strong className="text-[#1C1C1C]">50%</strong> of rent due on signing
                  </p>
                  <p>
                    Balance due <strong className="text-[#1C1C1C]">90 days</strong> before rental
                  </p>
                  <p className="text-sm">
                    Cancel anytime before 90 days before rental and your deposit 
                    will be forfeited. After 90 days, the deposit is non-refundable.
                  </p>
                </div>
              </div>

              <div className="bg-white p-8 rounded-sm shadow-sm">
                <h3 className="font-serif text-xl text-[#1C1C1C] mb-4">HST Information</h3>
                <div className="space-y-4 text-[#66605B]">
                  <p>
                    For rentals <strong className="text-[#1C1C1C]">greater than 30 days</strong>: 
                    No HST charged
                  </p>
                  <p>
                    For rentals <strong className="text-[#1C1C1C]">less than 30 days</strong>: 
                    HST will be charged
                  </p>
                </div>
              </div>

              <div className="bg-white p-8 rounded-sm shadow-sm">
                <h3 className="font-serif text-xl text-[#1C1C1C] mb-4">Security Deposit</h3>
                <div className="space-y-4 text-[#66605B]">
                  <p>
                    A signed credit card security deposit is required at check-in.
                  </p>
                  <p className="text-sm">
                    Minor breakage from normal wear and tear is not charged. 
                    The deposit is normally fully refunded after confirmation 
                    that everything is as it should be.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info Box */}
      <section className="container-padding pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#1B4D3E]/5 border border-[#1B4D3E]/20 p-6 rounded-sm flex gap-4">
            <Info className="w-6 h-6 text-[#1B4D3E] flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-serif text-lg text-[#1C1C1C] mb-2">Important Notes</h4>
              <ul className="text-[#66605B] space-y-2 text-sm">
                <li>• Big Bear Estate rents by the week in summer season</li>
                <li>• Weekend-only rentals are not available</li>
                <li>• Multiple-week bookings take precedence</li>
                <li>• Maximum occupancy: 15 persons (including day guests)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1B4D3E] text-white section-padding">
        <div className="container-padding text-center">
          <h2 className="font-serif text-3xl md:text-4xl mb-4">
            Ready to Book?
          </h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8">
            Check our availability calendar and submit an inquiry. 
            We'll get back to you within 24-48 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/booking"
              className="bg-[#D4AF37] text-[#1C1C1C] px-8 py-4 rounded-sm uppercase tracking-widest text-xs font-bold hover:bg-[#c9a432] transition-colors inline-flex items-center justify-center gap-2"
              data-testid="pricing-cta-booking"
            >
              Check Availability
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:416-543-4749"
              className="border border-white text-white px-8 py-4 rounded-sm uppercase tracking-widest text-xs font-bold hover:bg-white hover:text-[#1B4D3E] transition-colors"
            >
              Call 416.543.4749
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
